import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  RefreshCw, Calendar, DollarSign, TrendingUp, Clock, 
  Play, Pause, XCircle, FileText, Users, Filter,
  ChevronRight, AlertCircle, CheckCircle
} from 'lucide-react';

const RecurringChargesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filter, setFilter] = useState({ status: 'active', billing_cycle: '' });
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, subsRes] = await Promise.all([
        api.get('/api/subscriptions/dashboard'),
        api.get('/api/subscriptions', { 
          params: { 
            status: filter.status || undefined,
            billing_cycle: filter.billing_cycle || undefined
          }
        })
      ]);
      setDashboard(dashRes.data);
      setSubscriptions(subsRes.data.subscriptions || []);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async (subscriptionId) => {
    setActionLoading(prev => ({ ...prev, [subscriptionId]: 'invoice' }));
    try {
      const res = await api.post(`/api/subscriptions/${subscriptionId}/generate-invoice`);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setActionLoading(prev => ({ ...prev, [subscriptionId]: null }));
    }
  };

  const handleChargeNow = async (subscriptionId) => {
    setActionLoading(prev => ({ ...prev, [subscriptionId]: 'charge' }));
    try {
      const res = await api.post(`/api/payments/subscriptions/${subscriptionId}/charge-now`, {
        origin_url: window.location.origin,
        payment_method: 'cc',
        created_by: 'admin_manual_charge'
      });

      if (res.data.mode === 'checkout' && res.data.url) {
        window.location.href = res.data.url;
        return;
      }

      toast.success(`Subscription charged successfully${res.data.invoice_number ? ` (${res.data.invoice_number})` : ''}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to charge subscription');
    } finally {
      setActionLoading(prev => ({ ...prev, [subscriptionId]: null }));
    }
  };

  const handlePause = async (subscriptionId) => {
    setActionLoading(prev => ({ ...prev, [subscriptionId]: 'pause' }));
    try {
      await api.put(`/api/subscriptions/${subscriptionId}/pause`);
      toast.success('Subscription paused');
      fetchData();
    } catch (error) {
      toast.error('Failed to pause subscription');
    } finally {
      setActionLoading(prev => ({ ...prev, [subscriptionId]: null }));
    }
  };

  const handleResume = async (subscriptionId) => {
    setActionLoading(prev => ({ ...prev, [subscriptionId]: 'resume' }));
    try {
      await api.put(`/api/subscriptions/${subscriptionId}/resume`);
      toast.success('Subscription resumed');
      fetchData();
    } catch (error) {
      toast.error('Failed to resume subscription');
    } finally {
      setActionLoading(prev => ({ ...prev, [subscriptionId]: null }));
    }
  };

  const handleCancel = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    setActionLoading(prev => ({ ...prev, [subscriptionId]: 'cancel' }));
    try {
      await api.put(`/api/subscriptions/${subscriptionId}/cancel`);
      toast.success('Subscription cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(prev => ({ ...prev, [subscriptionId]: null }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isDueSoon = (nextBillingDate) => {
    if (!nextBillingDate) return false;
    const dueDate = new Date(nextBillingDate);
    const now = new Date();
    const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getBillingBadge = (cycle) => {
    if (cycle === 'monthly') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
          <RefreshCw className="w-3 h-3 mr-1" /> Monthly
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
        <Calendar className="w-3 h-3 mr-1" /> Yearly
      </span>
    );
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="recurring-charges-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Recurring Charges</h1>
          <p className="text-gray-500">Manage subscriptions and recurring billing</p>
        </div>
        <Button onClick={fetchData} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* MRR */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Monthly Recurring</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard.mrr)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{dashboard.monthly_subscriptions} active subscriptions</p>
          </div>

          {/* ARR */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Annual Recurring</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard.arr)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{dashboard.yearly_subscriptions} yearly subscriptions</p>
          </div>

          {/* Due This Week */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Due This Week</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard.due_next_7_days?.total)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{dashboard.due_next_7_days?.count} charges pending</p>
          </div>

          {/* Due This Month */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Due This Month</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard.due_next_30_days?.total)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{dashboard.due_next_30_days?.count} charges pending</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 p-4">
        <Filter className="w-5 h-5 text-gray-400" />
        <select
          value={filter.status}
          onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={filter.billing_cycle}
          onChange={(e) => setFilter(prev => ({ ...prev, billing_cycle: e.target.value }))}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Billing Cycles</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Subscriptions ({subscriptions.length})</h2>
        </div>

        {subscriptions.length === 0 ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No subscriptions found</p>
            <p className="text-sm text-gray-400 mt-1">Subscriptions are created when invoices with recurring items are paid</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billing</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Billing</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className={`hover:bg-gray-50 ${isDueSoon(sub.next_billing_date) ? 'bg-orange-50' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{sub.client_name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm text-gray-900">{sub.item_description}</p>
                        <p className="text-xs text-gray-400">Qty: {sub.quantity}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getBillingBadge(sub.billing_cycle)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-semibold text-gray-900">{formatCurrency(sub.total)}</span>
                      <span className="text-xs text-gray-400">/{sub.billing_cycle === 'monthly' ? 'mo' : 'yr'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {isDueSoon(sub.next_billing_date) && (
                          <AlertCircle className="w-4 h-4 text-orange-500" />
                        )}
                        <span className={isDueSoon(sub.next_billing_date) ? 'text-orange-600 font-medium' : 'text-gray-600'}>
                          {formatDate(sub.next_billing_date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(sub.status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {sub.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGenerateInvoice(sub.id)}
                              disabled={actionLoading[sub.id]}
                              className="h-8 text-xs"
                            >
                              {actionLoading[sub.id] === 'invoice' ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <FileText className="w-3 h-3 mr-1" />
                              )}
                              Invoice
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleChargeNow(sub.id)}
                              disabled={actionLoading[sub.id]}
                              className="h-8 text-xs bg-[#014DB7] hover:bg-[#013a8c]"
                              data-testid={`subscription-charge-now-${sub.id}`}
                            >
                              {actionLoading[sub.id] === 'charge' ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <DollarSign className="w-3 h-3 mr-1" />
                              )}
                              Charge Now
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePause(sub.id)}
                              disabled={actionLoading[sub.id]}
                              className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700"
                              title="Pause"
                            >
                              <Pause className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancel(sub.id)}
                              disabled={actionLoading[sub.id]}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {sub.status === 'paused' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResume(sub.id)}
                              disabled={actionLoading[sub.id]}
                              className="h-8 text-xs text-green-600"
                            >
                              {actionLoading[sub.id] === 'resume' ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Play className="w-3 h-3 mr-1" />
                              )}
                              Resume
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancel(sub.id)}
                              disabled={actionLoading[sub.id]}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {sub.status === 'cancelled' && (
                          <span className="text-xs text-gray-400">Cancelled</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">How Recurring Billing Works</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>When an invoice with monthly/yearly items is paid, subscriptions are automatically created</li>
          <li>Subscriptions track the next billing date based on their cycle</li>
          <li>Click "Invoice" to generate a new invoice for that subscription</li>
          <li>Pause subscriptions to temporarily stop billing without cancelling</li>
        </ul>
      </div>
    </div>
  );
};

export default RecurringChargesPage;
